/**
 * Predict 代币授权工具
 * 用于查询和构建 USDC/CTF 授权交易
 */

import { createPublicClient, http, encodeFunctionData, erc20Abi } from 'viem';
import { polygon } from 'viem/chains';
import { type Transaction } from './predict-relayer';
import { CHAIN_RPC_URLS } from './rpc-utils';

// ============ 常量配置 ============

/** Polygon RPC URL */
const POLYGON_RPC_URL = process.env.NEXT_PUBLIC_POLYGON_RPC_URL || CHAIN_RPC_URLS[137];

/** 合约地址 */
const CONTRACTS = {
  /** USDC.e 代币合约(V1 抵押币) */
  usdc: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174' as const,
  usdcDecimals: 6,
  /** pUSD 代币合约(V2 抵押币,proxy) */
  pusd: '0xC011a7E12a19f7B1f670d46F03B03f3342E82DFB' as const,
  pusdDecimals: 6,
  /** CTF (Conditional Token Framework) 合约 - ERC1155(V1/V2 共用) */
  ctf: '0x4d97dcd97ec945f40cf65f87097ace5ea0476045' as const,
  /** CTF Exchange V1 合约 */
  ctfExchangeV1: '0x4bFb41d5B3570DeFd03C39a9A4D8dE6Bd8B8982E' as const,
  /** CTF Exchange V2 合约(04-28 后生效) */
  ctfExchangeV2: '0xE111180000d2663C0091e4f400237545B87B996B' as const,
  /** Neg Risk CTF Exchange V1 合约 */
  negRiskCtfExchangeV1: '0xC5d563A36AE78145C45a50134d48A1215220f80a' as const,
  /** Neg Risk CTF Exchange V2 合约 */
  negRiskCtfExchangeV2: '0xe2222d279d744050d28e00520010520000310F59' as const,
  /** Neg Risk Adapter 合约(V1/V2 共用) */
  negRiskAdapter: '0xd91E80cF2E7be2e162c6513ceD06f1dD0dA35296' as const,
  /** Permit2 合约(共用) */
  permit2: '0x000000000022D473030F116dDEE9F6B43aC78BA3' as const,
  /** CollateralOnramp 合约(USDC.e → pUSD wrap) */
  collateralOnramp: '0x93070a847efEf7F70739046A929D47a521F5B8ee' as const,
  /** CollateralOfframp 合约(pUSD → USDC.e unwrap) */
  collateralOfframp: '0x2957922Eb93258b93368531d39fAcCA3B4dC5854' as const,
};

/** 最大授权额度 */
const MAX_UINT256 = BigInt(
  '115792089237316195423570985008687907853269984665640564039457584007913129639935'
);

/** V1 抵押代币(USDC.e)授权清单 */
const V1_COLLATERAL_SPENDERS = [
  { address: CONTRACTS.ctf, token: CONTRACTS.usdc, name: 'CTF Contract', key: 'ctf' },
  { address: CONTRACTS.ctfExchangeV1, token: CONTRACTS.usdc, name: 'CTF Exchange V1', key: 'ctfExchangeV1' },
  { address: CONTRACTS.negRiskCtfExchangeV1, token: CONTRACTS.usdc, name: 'NegRisk Exchange V1', key: 'negRiskExchangeV1' },
  { address: CONTRACTS.negRiskAdapter, token: CONTRACTS.usdc, name: 'NegRisk Adapter', key: 'negRiskAdapter' },
  { address: CONTRACTS.permit2, token: CONTRACTS.usdc, name: 'Permit2', key: 'permit2' },
] as const;

/** V2 抵押代币(pUSD)授权清单 */
const V2_COLLATERAL_SPENDERS = [
  { address: CONTRACTS.ctfExchangeV2, token: CONTRACTS.pusd, name: 'CTF Exchange V2', key: 'ctfExchangeV2' },
  { address: CONTRACTS.negRiskCtfExchangeV2, token: CONTRACTS.pusd, name: 'NegRisk Exchange V2', key: 'negRiskExchangeV2' },
  { address: CONTRACTS.negRiskAdapter, token: CONTRACTS.pusd, name: 'NegRisk Adapter', key: 'negRiskAdapter' },
  { address: CONTRACTS.permit2, token: CONTRACTS.pusd, name: 'Permit2', key: 'permit2' },
] as const;

/** V1 CTF (ERC1155) 操作员清单 */
const V1_CTF_OPERATORS = [
  { address: CONTRACTS.ctfExchangeV1, name: 'CTF Exchange V1', key: 'ctfExchangeV1' },
  { address: CONTRACTS.negRiskCtfExchangeV1, name: 'NegRisk Exchange V1', key: 'negRiskExchangeV1' },
  { address: CONTRACTS.negRiskAdapter, name: 'NegRisk Adapter', key: 'negRiskAdapter' },
] as const;

/** V2 CTF (ERC1155) 操作员清单 */
const V2_CTF_OPERATORS = [
  { address: CONTRACTS.ctfExchangeV2, name: 'CTF Exchange V2', key: 'ctfExchangeV2' },
  { address: CONTRACTS.negRiskCtfExchangeV2, name: 'NegRisk Exchange V2', key: 'negRiskExchangeV2' },
  { address: CONTRACTS.negRiskAdapter, name: 'NegRisk Adapter', key: 'negRiskAdapter' },
] as const;

/** 按 version 选 collateral spenders 清单 */
export function getCollateralSpenders(version: number) {
  return version === 1 ? V1_COLLATERAL_SPENDERS : V2_COLLATERAL_SPENDERS;
}

/** 按 version 选 CTF operators 清单 */
export function getCtfOperators(version: number) {
  return version === 1 ? V1_CTF_OPERATORS : V2_CTF_OPERATORS;
}

// 向后兼容别名(让 Phase 2b 渐进迁移时不破坏现有 import)
export const USDC_SPENDERS = V1_COLLATERAL_SPENDERS;
export const CTF_OPERATORS = V1_CTF_OPERATORS;

// ============ ERC1155 ABI ============

const erc1155Abi = [
  {
    inputs: [
      { name: 'operator', type: 'address' },
      { name: 'approved', type: 'bool' },
    ],
    name: 'setApprovalForAll',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      { name: 'account', type: 'address' },
      { name: 'operator', type: 'address' },
    ],
    name: 'isApprovedForAll',
    outputs: [{ name: '', type: 'bool' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const;

// ============ Viem 客户端 ============

const publicClient = createPublicClient({
  chain: polygon,
  transport: http(POLYGON_RPC_URL),
});

// ============ 类型定义 ============

/** USDC 授权状态类型 */
export interface UsdcAllowance {
  spender: string;
  name: string;
  key: string;
  token: string; // 新增:USDC.e 或 pUSD 地址,便于区分 V1/V2 抵押币
  allowance: bigint;
  allowanceFormatted: string;
}

/** CTF 授权状态类型 */
export interface CtfApproval {
  operator: string;
  name: string;
  key: string;
  approved: boolean;
}

// ============ 查询方法 ============

/**
 * 查询任意 ERC20 代币授权额度(V1 用 USDC.e,V2 用 pUSD)
 */
async function getErc20Allowance(tokenAddress: string, ownerAddress: string, spenderAddress: string): Promise<bigint> {
  try {
    const allowance = await publicClient.readContract({
      address: tokenAddress as `0x${string}`,
      abi: erc20Abi,
      functionName: 'allowance',
      args: [ownerAddress as `0x${string}`, spenderAddress as `0x${string}`],
    });
    return allowance;
  } catch (error) {
    console.warn(`[Approvals] Failed to get allowance for token ${tokenAddress} → spender ${spenderAddress}:`, error);
    return BigInt(0);
  }
}

// 向后兼容
const getUsdcAllowance = (owner: string, spender: string) => getErc20Allowance(CONTRACTS.usdc, owner, spender);

/**
 * 查询单个 CTF 授权状态
 */
async function getCtfApproval(ownerAddress: string, operatorAddress: string): Promise<boolean> {
  try {
    const isApproved = await publicClient.readContract({
      address: CONTRACTS.ctf,
      abi: erc1155Abi,
      functionName: 'isApprovedForAll',
      args: [ownerAddress as `0x${string}`, operatorAddress as `0x${string}`],
    });
    return isApproved;
  } catch (error) {
    console.warn(`[Approvals] Failed to get CTF approval for ${operatorAddress}:`, error);
    return false;
  }
}

/**
 * 格式化 USDC 额度显示
 */
function formatUsdcAllowance(allowance: bigint): string {
  if (allowance >= MAX_UINT256 / BigInt(2)) {
    return 'Unlimited';
  }
  const decimals = CONTRACTS.usdcDecimals;
  const value = Number(allowance) / Math.pow(10, decimals);
  if (value >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(2)}B`;
  }
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(2)}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(2)}K`;
  }
  return value.toFixed(2);
}

/**
 * 查询所有抵押代币授权额度(按 version 选 USDC.e 或 pUSD)
 */
export async function getAllCollateralAllowances(safeAddress: string, version: number): Promise<UsdcAllowance[]> {
  const spenders = getCollateralSpenders(version);
  const results = await Promise.all(
    spenders.map(async ({ address, token, name, key }) => {
      const allowance = await getErc20Allowance(token, safeAddress, address);
      return {
        spender: address,
        name,
        key,
        token,
        allowance,
        allowanceFormatted: formatUsdcAllowance(allowance),
      };
    })
  );
  return results;
}

// 向后兼容别名(Phase 2b 完全切换后可删)
export const getAllUsdcAllowances = (safe: string) => getAllCollateralAllowances(safe, 1);

/**
 * 查询所有 CTF 授权状态(按 version 路由 V1/V2 operator 清单)
 */
export async function getAllCtfApprovals(safeAddress: string, version: number): Promise<CtfApproval[]> {
  const operators = getCtfOperators(version);
  const results = await Promise.all(
    operators.map(async ({ address, name, key }) => {
      const approved = await getCtfApproval(safeAddress, address);
      return {
        operator: address,
        name,
        key,
        approved,
      };
    })
  );
  return results;
}

// ============ 交易构建方法 ============

/**
 * 创建任意 ERC20 授权交易
 */
export function createErc20ApproveTx(tokenAddress: string, spenderAddress: string, amount: bigint): Transaction {
  return {
    to: tokenAddress,
    data: encodeFunctionData({
      abi: erc20Abi,
      functionName: 'approve',
      args: [spenderAddress as `0x${string}`, amount],
    }),
    value: '0',
  };
}

// 向后兼容别名
export const createUsdcApproveTx = (spender: string, amount: bigint) => createErc20ApproveTx(CONTRACTS.usdc, spender, amount);
export const createPusdApproveTx = (spender: string, amount: bigint) => createErc20ApproveTx(CONTRACTS.pusd, spender, amount);

/**
 * 创建 CTF 授权交易
 */
export function createCtfApproveTx(operatorAddress: string, approved: boolean): Transaction {
  return {
    to: CONTRACTS.ctf,
    data: encodeFunctionData({
      abi: erc1155Abi,
      functionName: 'setApprovalForAll',
      args: [operatorAddress as `0x${string}`, approved],
    }),
    value: '0',
  };
}

/**
 * 创建所有授权交易(按 version 一次性授权所有 V1 或 V2 spenders + operators)
 */
export function createAllApprovalTxs(version: number, amount?: bigint): Transaction[] {
  const spenders = getCollateralSpenders(version);
  const operators = getCtfOperators(version);
  const approveAmount = amount ?? MAX_UINT256;
  const txs: Transaction[] = [];

  // 抵押代币授权(V1=USDC.e / V2=pUSD)
  for (const { address, token } of spenders) {
    txs.push(createErc20ApproveTx(token, address, approveAmount));
  }

  // CTF (ERC1155) 授权
  for (const { address } of operators) {
    txs.push(createCtfApproveTx(address, true));
  }

  return txs;
}

/**
 * 解析 USDC 输入金额为 wei
 */
export function parseUsdcAmount(amount: string): bigint {
  if (amount.toLowerCase() === 'max' || amount.toLowerCase() === 'unlimited') {
    return MAX_UINT256;
  }
  const decimals = CONTRACTS.usdcDecimals;
  const value = parseFloat(amount);
  if (isNaN(value) || value < 0) {
    throw new Error('Invalid amount');
  }
  return BigInt(Math.floor(value * Math.pow(10, decimals)));
}

// ============ 导出常量 ============

export { MAX_UINT256, CONTRACTS };
